import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from '../hooks/useInView';

export default function EventsShowcase() {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [currentSlide, setCurrentSlide] = useState(0);

  const events = [
    {
      category: 'MOTORSPORT',
      title: 'Formula Racing Championship',
      description: 'Organizzazione completa di eventi motorsport di livello internazionale con gestione logistica e coordinamento team.',
      image: 'https://public.readdy.ai/ai/img_res/8b52df0391e7966ae74be44001af399d.jpg',
    },
    {
      category: 'CORPORATE',
      title: 'International Business Summit',
      description: 'Conferenza internazionale con oltre 500 partecipanti, gestione completa dalla logistica al catering di lusso.',
      image: 'https://readdy.ai/api/search-image?query=luxury%20international%20business%20summit%20with%20elegant%20stage%20setup%20professional%20conference%20with%20hundreds%20of%20attendees%20in%20modern%20venue%20sophisticated%20lighting%20and%20corporate%20atmosphere%20high-end%20event%20photography&width=1400&height=800&seq=event2&orientation=landscape',
    },
    {
      category: 'FIERA',
      title: 'Tech Innovation Expo',
      description: 'Evento fieristico tecnologico con stand innovativi, area networking e presentazioni prodotto di alto livello.',
      image: 'https://readdy.ai/api/search-image?query=modern%20technology%20innovation%20expo%20with%20futuristic%20exhibition%20stands%20professional%20trade%20show%20with%20innovative%20displays%20and%20interactive%20areas%20contemporary%20design%20with%20dramatic%20lighting&width=1400&height=800&seq=event3&orientation=landscape',
    },
    {
      category: 'MOTORSPORT',
      title: 'Rally Championship Series',
      description: 'Coordinamento eventi rally con gestione percorsi, sicurezza e hospitality per team e sponsor internazionali.',
      image: 'https://readdy.ai/api/search-image?query=professional%20rally%20championship%20with%20racing%20car%20on%20dirt%20track%20dramatic%20action%20photography%20with%20dust%20and%20motion%20blur%20motorsport%20event%20with%20dynamic%20composition%20and%20vibrant%20atmosphere&width=1400&height=800&seq=event4&orientation=landscape',
    },
  ];

  const upcomingEvents = [
    { date: { day: '15', month: 'FEB' }, name: 'Automotive Expo Milano', location: 'Milano, Italia' },
    { date: { day: '22', month: 'MAR' }, name: 'Corporate Leadership Summit', location: 'Roma, Italia' },
    { date: { day: '10', month: 'APR' }, name: 'GT Racing Championship', location: 'Monza, Italia' },
    { date: { day: '05', month: 'MAG' }, name: 'Innovation Tech Fair', location: 'Bologna, Italia' },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % events.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + events.length) % events.length);
  };

  return (
    <section id="eventi" ref={ref} className="py-24 md:py-32 bg-dark relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Carousel */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative h-[600px] rounded-3xl overflow-hidden"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0"
                >
                  <div className="w-full h-full">
                    <img
                      src={events[currentSlide].image}
                      alt={events[currentSlide].title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                    <div className="inline-block bg-primary px-4 py-2 rounded-full text-xs font-bold uppercase mb-4 whitespace-nowrap">
                      {events[currentSlide].category}
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      {events[currentSlide].title}
                    </h3>
                    <p className="text-white/80 text-base md:text-lg mb-6 max-w-2xl">
                      {events[currentSlide].description}
                    </p>
                    <button className="bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary/90 transition-all whitespace-nowrap">
                      VEDI PROGETTO
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full border-2 border-white/50 text-white flex items-center justify-center hover:bg-primary hover:border-primary transition-all z-10 cursor-pointer"
                aria-label="Evento precedente"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full border-2 border-white/50 text-white flex items-center justify-center hover:bg-primary hover:border-primary transition-all z-10 cursor-pointer"
                aria-label="Evento successivo"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>

              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                {events.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all cursor-pointer ${
                      index === currentSlide ? 'bg-primary w-8' : 'bg-white/50 w-2'
                    }`}
                    aria-label={`Vai all'evento ${index + 1}`}
                  ></button>
                ))}
              </div>
            </motion.div>

            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-4 mt-6">
              {events.map((event, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`relative h-24 rounded-xl overflow-hidden cursor-pointer transition-all ${
                    index === currentSlide ? 'ring-4 ring-primary' : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover object-top"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Upcoming Events Panel */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-black/40 backdrop-blur-lg rounded-3xl p-8"
          >
            <h3 className="text-2xl font-bold text-white mb-8">Prossimi Eventi</h3>
            <div className="space-y-6">
              {upcomingEvents.map((event, index) => (
                <div key={index} className="flex gap-4 pb-6 border-b border-white/10 last:border-0">
                  <div className="flex flex-col items-center justify-center bg-primary/20 rounded-xl px-4 py-3 min-w-[70px]">
                    <div className="text-2xl font-bold text-primary">{event.date.day}</div>
                    <div className="text-xs text-white/70 uppercase">{event.date.month}</div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-2">{event.name}</h4>
                    <div className="flex items-center gap-2 text-white/60 text-sm">
                      <i className="ri-map-pin-line"></i>
                      <span>{event.location}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}